import { calendarWithKoreanHolidays } from "./calendar";
import { createSampleContentProject, createSampleQuoteProject, createSampleSafetyProject } from "./sample-companions";
import { sampleErpDocumentLinks, sampleErpDocuments } from "./sample-documents";
import { createSampleMarketingProject } from "./sample-marketing";
import { sampleOrganizationTeams, sampleUserResources } from "./sample-org";
import { sampleErpRecords } from "./sample-records";
import { createSampleRobotProject } from "./sample-robot";
import type {
  CostOptions,
  Dependency,
  DependencyType,
  ExecutionTask,
  GateChecklistItem,
  GateTag,
  GateTagStatus,
  GateTagType,
  IntakeItem,
  OrganizationTeam,
  PlannerSettings,
  Project,
  ProjectBudget,
  ProjectRecord,
  ProjectResourceAssignment,
  SolutionItem,
  Task,
  UserResource,
} from "./types";

export { sampleOrganizationTeams, sampleUserResources } from "./sample-org";
export { createSampleRobotProject } from "./sample-robot";
export { createSampleMarketingProject } from "./sample-marketing";
export { createSampleContentProject, createSampleQuoteProject, createSampleSafetyProject } from "./sample-companions";

export function defaultCostOptions(): CostOptions {
  return {
    workingDaysPerMonth: 21,
    includeOverhead: true,
    overheadRatio: 1.1,
    includeTechnicalFee: true,
    technicalFeeRatio: 0.2,
    hideRatesByDefault: false,
  };
}

export function sampleErpAssignments(): ProjectResourceAssignment[] {
  return [
    {
      id: "asg_erp_kim",
      resourceId: "res_kim_pm",
      projectRole: "총괄 PM (사업책임자)",
      accessRole: "pm",
      startDate: "2026-09-01",
      endDate: "2027-03-31",
      allocationRatio: 1.0,
      plannedMM: 7.0,
    },
    {
      id: "asg_erp_me",
      resourceId: "res_me",
      projectRole: "사업 소유자",
      accessRole: "admin",
      startDate: "2026-09-01",
      endDate: "2027-03-31",
      allocationRatio: 0.3,
      plannedMM: 2.1,
    },
    {
      id: "asg_erp_choi",
      resourceId: "res_choi_plan",
      projectRole: "업무분석 파트장 (BA)",
      accessRole: "pl",
      startDate: "2026-09-01",
      endDate: "2026-12-15",
      allocationRatio: 0.8,
      plannedMM: 2.8,
    },
    {
      id: "asg_erp_lee",
      resourceId: "res_lee_arch",
      projectRole: "공통 아키텍처 PL",
      accessRole: "pl",
      startDate: "2026-09-15",
      endDate: "2026-12-31",
      allocationRatio: 1.0,
      plannedMM: 3.5,
    },
    {
      id: "asg_erp_park",
      resourceId: "res_park_dev",
      projectRole: "생산·구매 코어모듈 개발 (협력사)",
      accessRole: "member",
      startDate: "2026-10-01",
      endDate: "2027-02-28",
      allocationRatio: 1.0,
      plannedMM: 5.0,
    },
    {
      id: "asg_erp_jung",
      resourceId: "res_jung_front",
      projectRole: "UI/UX 포털 화면 개발",
      accessRole: "member",
      startDate: "2026-10-15",
      endDate: "2027-02-15",
      allocationRatio: 1.0,
      plannedMM: 4.0,
    },
    {
      id: "asg_erp_kang",
      resourceId: "res_kang_infra",
      projectRole: "인프라 구축 및 배포 자동화",
      accessRole: "member",
      startDate: "2026-09-01",
      endDate: "2026-11-30",
      allocationRatio: 0.5,
      plannedMM: 1.5,
    },
      {
        id: "asg_erp_song",
        resourceId: "res_song_dba",
        projectRole: "데이터 모델링 및 이관",
        accessRole: "member",
        startDate: "2026-09-15",
        endDate: "2027-01-31",
        allocationRatio: 0.6,
        plannedMM: 2.7,
      },
      {
        id: "asg_erp_oh",
        resourceId: "res_oh_qa",
        projectRole: "통합·고객 인수 검수",
        accessRole: "member",
        startDate: "2026-12-01",
        endDate: "2027-03-31",
        allocationRatio: 0.4,
        plannedMM: 1.6,
      },
      {
        id: "asg_erp_shin",
        resourceId: "res_shin_spec",
        projectRole: "제조 DX 자문",
        accessRole: "viewer",
        startDate: "2026-09-01",
        endDate: "2027-03-31",
        allocationRatio: 0.15,
        plannedMM: 1.0,
      },
    ];
  }

export function sampleErpSolutions(): SolutionItem[] {
  return [
    {
      id: "sol_oracle",
      name: "Oracle Enterprise DB (Core 4)",
      category: "software",
      vendor: "한국오라클",
      billingCycle: "yearly",
      unitCost: 32000000,
      quantity: 1,
      totalCost: 32000000,
      startDate: "2026-09-01",
      endDate: "2027-08-31",
      assignedResourceId: "res_song_dba",
      status: "contracted",
      notes: "ERP 기간계 코어 트랜잭션 RDBMS 라이선스",
    },
    {
      id: "sol_aws",
      name: "AWS 클라우드 인프라 (개발/검증 환경)",
      category: "cloud",
      vendor: "Amazon Web Services",
      billingCycle: "monthly",
      unitCost: 4500000,
      quantity: 7,
      totalCost: 31500000,
      startDate: "2026-09-01",
      endDate: "2027-03-31",
      assignedResourceId: "res_kang_infra",
      status: "active",
      notes: "EKS 클러스터, RDS Dev, NAT Gateway 등 7개월치",
    },
    {
      id: "sol_figma",
      name: "Figma Enterprise Org 라이선스",
      category: "saas",
      vendor: "Figma Inc",
      billingCycle: "monthly",
      unitCost: 75000,
      quantity: 8,
      totalCost: 4200000,
      startDate: "2026-09-01",
      endDate: "2027-03-31",
      assignedResourceId: "res_choi_plan",
      status: "active",
      notes: "화면설계 및 디자인 시스템 공동 작업 라이선스",
    },
    {
      id: "sol_sec_bridge",
      name: "망연계 보안 솔루션 라이선스 (MES 연계용)",
      category: "license",
      vendor: "시큐어브릿지",
      billingCycle: "once",
      unitCost: 16000000,
      quantity: 1,
      totalCost: 16000000,
      startDate: "2026-10-01",
      assignedResourceId: "res_lee_arch",
      status: "contracted",
      notes: "생산 공장 폐쇄망과 ERP 서버 간 실시간 중계",
    },
    {
      id: "sol_atlassian",
      name: "Atlassian Jira + Confluence (Cloud Standard)",
      category: "saas",
      vendor: "Atlassian",
      billingCycle: "monthly",
      unitCost: 12000,
      quantity: 56,
      totalCost: 672000,
      startDate: "2026-09-01",
      endDate: "2027-03-31",
      assignedResourceId: "res_kim_pm",
      status: "active",
      notes: "8인 × 7개월 · 이슈 트래킹 및 산출물 위키",
    },
    {
      id: "sol_dev_server",
      name: "개발·검증용 온프레미스 서버 2식",
      category: "hardware",
      vendor: "델 테크놀로지스",
      billingCycle: "once",
      unitCost: 9800000,
      quantity: 2,
      totalCost: 19600000,
      startDate: "2026-10-15",
      assignedResourceId: "res_kang_infra",
      status: "planned",
      notes: "폐쇄망 연계 테스트용 · 프로젝트 종료 후 고객사 이관",
    },
    {
      id: "sol_sonarqube",
      name: "SonarQube Developer Edition",
      category: "software",
      vendor: "SonarSource",
      billingCycle: "yearly",
      unitCost: 5400000,
      quantity: 1,
      totalCost: 5400000,
      startDate: "2026-11-01",
      endDate: "2027-10-31",
      assignedResourceId: "res_lee_arch",
      status: "planned",
      notes: "코드 검사와 산출물 검수 기준",
    },
  ];
}

export function sampleErpBudget(): ProjectBudget {
  return {
    totalBudget: 580000000,
    targetLaborCost: 420000000,
    targetSolutionCost: 90000000,
    targetExpenseCost: 40000000,
    contingencyCost: 30000000,
    costOptions: defaultCostOptions(),
  };
}

function samplePlanner(): PlannerSettings {
  return {
    buckets: [
      { id: "b_backlog", name: "백로그" },
      { id: "b_week", name: "이번 주" },
      { id: "b_review", name: "검토 대기" },
      { id: "b_done", name: "끝난 일" },
    ],
    labels: [
      { id: "l_bug", name: "결함", color: "red" },
      { id: "l_review", name: "리뷰 필요", color: "orange" },
      { id: "l_doc", name: "문서", color: "blue" },
      { id: "l_meeting", name: "회의", color: "purple" },
      { id: "l_data", name: "데이터", color: "teal" },
    ],
    customFields: [],
  };
}

function sampleCheck(id: string, text: string, extra: Partial<GateChecklistItem> = {}): GateChecklistItem {
  return { id, text, devChecked: false, qaStatus: "pending", ...extra };
}

function sampleGate(
  id: string,
  name: string,
  type: GateTagType,
  status: GateTagStatus,
  checklist: GateChecklistItem[],
  extra: Partial<GateTag> = {},
): GateTag {
  return { id, name, type, status, checklist, ...extra };
}

/** Execution items that hang off the ERP schedule: some fully planned, some deliberately undecided. */
function sampleExecutionTasks(): ExecutionTask[] {
  const t0 = "2026-10-12T09:00:00.000Z";
  const base = (id: string, title: string, extra: Partial<ExecutionTask>): ExecutionTask => ({
    id,
    title,
    assignees: [],
    linkedTaskIds: [],
    labelIds: [],
    checklist: [],
    attachments: [],
    comments: [],
    activity: [],
    customFields: {},
    order: 0,
    createdAt: t0,
    updatedAt: t0,
    ...extra,
  });
  return [
    base("x_gap_review", "GAP 분석 결과 현업 리뷰 회의", {
      status: "inProgress",
      priority: "important",
      bucketId: "b_week",
      assignees: ["김PM"],
      start: "2026-10-19",
      due: "2026-10-21",
      linkedTaskIds: ["p2", "t2-4"],
      labelIds: ["l_meeting", "l_review"],
      checklist: [
        { id: "ck1", text: "생산팀 일정 확인", done: true },
        { id: "ck2", text: "회의실 예약", done: true },
        { id: "ck3", text: "커스터마이징 목록 인쇄", done: false },
      ],
      notes: "구매·재무는 화상 참여. 결정 사항은 요구사항 정의서에 바로 반영.",
      attachments: [{ id: "at1", title: "GAP 분석 초안", url: "https://example.com/docs/gap-analysis", addedAt: t0, addedBy: "김PM" }],
      comments: [{ id: "cm1", author: "김PM", body: "생산팀은 20일 오후만 가능하다고 합니다.", at: "2026-10-13T02:10:00.000Z" }],
      gateTags: [
        sampleGate("g_gap", "품질 검수", "qa", "ready", [
          sampleCheck("gc_gap1", "생산·구매·재무 참석자가 회의록에 남는다", { devChecked: true }),
          sampleCheck("gc_gap2", "결정 사항이 요구사항 정의서 초안에 반영된다", { devChecked: true }),
        ]),
      ],
      order: 0,
    }),
    base("x_req_sign", "요구사항 정의서 서명 받기", {
      status: "notStarted",
      priority: "urgent",
      bucketId: "b_week",
      assignees: ["김PM", "박현업"],
      due: "2026-10-23",
      linkedTaskIds: ["t2-5", "m2"],
      labelIds: ["l_doc"],
      originIntakeId: "in_sign",
      notes: "한빛제조 정발주가 범위와 일정을 확인한 뒤에 설계를 연다.",
      gateTags: [
        sampleGate("g_sign_qa", "품질 검수", "qa", "unverified", [
          sampleCheck("gc_sign1", "고객 서명이 있는 PDF가 문서에 첨부된다"),
        ]),
        sampleGate("g_sign_uat", "고객 인수", "uat", "unverified", [
          sampleCheck("gc_sign2", "정발주가 범위와 일정을 확인했다고 적는다"),
        ]),
      ],
      order: 1,
    }),
    base("x_arch_options", "아키텍처 대안 3개 비교표", {
      status: "notStarted",
      priority: "medium",
      bucketId: "b_backlog",
      assignees: ["이아키"],
      linkedTaskIds: ["t3-1"],
      labelIds: ["l_doc"],
      checklist: [
        { id: "ck4", text: "온프레미스", done: false },
        { id: "ck5", text: "클라우드", done: false },
        { id: "ck6", text: "하이브리드", done: false },
      ],
      order: 0,
    }),
    base("x_mes_if", "MES 인터페이스 항목 확인 요청 — 레거시 WMS 실시간 연동 규격 검토", {
      priority: "important",
      bucketId: "b_backlog",
      linkedTaskIds: ["t3-4"],
      labelIds: ["l_data"],
      notes: "담당자 미정 — MES 벤더 기술지원팀 회신 접수 후 현업 아키텍처 파트와 협의하여 담당자 배정 예정.",
      order: 1,
    }),
    base("x_weekly", "주간 진척 보고", {
      status: "notStarted",
      priority: "medium",
      bucketId: "b_week",
      assignees: ["김PM"],
      start: "2026-10-23",
      due: "2026-10-23",
      labelIds: ["l_doc"],
      order: 2,
    }),
    base("x_kickoff_minutes", "킥오프 회의록 배포", {
      status: "done",
      priority: "low",
      bucketId: "b_done",
      assignees: ["최기획"],
      start: "2026-09-02",
      due: "2026-09-03",
      completedAt: "2026-09-03T08:00:00.000Z",
      linkedTaskIds: ["t1-1"],
      labelIds: ["l_doc"],
      gateTags: [
        sampleGate(
          "g_kickoff",
          "품질 검수",
          "qa",
          "passed",
          [sampleCheck("gc_kick1", "참석자와 결정이 회의록에 있다", { devChecked: true, qaStatus: "pass" })],
          { verifiedBy: "최기획", verifiedAt: "2026-09-03T08:00:00.000Z" },
        ),
      ],
      order: 0,
    }),
    base("x_interview_bug", "인터뷰 녹취 파일 재무팀 분 누락", {
      status: "blocked",
      priority: "urgent",
      bucketId: "b_review",
      assignees: ["최기획"],
      due: "2026-10-16",
      linkedTaskIds: ["t2-1"],
      labelIds: ["l_bug", "l_data"],
      comments: [{ id: "cm2", author: "최기획", body: "녹음기 오류로 후반 30분이 없습니다. 재인터뷰 요청 중.", at: "2026-10-14T05:30:00.000Z" }],
      gateTags: [
        sampleGate(
          "g_bug",
          "품질 검수",
          "qa",
          "failed",
          [
            sampleCheck("gc_bug1", "재무팀 인터뷰 후반 30분이 녹취에 있다", {
              devChecked: true,
              qaStatus: "fail",
              failReason: "녹음기 오류로 후반 30분이 없습니다.",
            }),
          ],
          { failReason: "녹음기 오류로 후반 30분이 없습니다.", verifiedBy: "오품질", verifiedAt: "2026-10-16T02:00:00.000Z" },
        ),
      ],
      order: 0,
    }),
    base("x_idea", "데이터 이관 리허설 체크리스트 초안", {
      linkedTaskIds: [],
      notes: "어느 단계에 붙일지 미정. 이관 담당 정해지면 연결.",
      order: 2,
    }),
    base("x_me_draft", "10월 2주차 현황 초안 마감", {
      status: "inProgress",
      priority: "important",
      bucketId: "b_week",
      assignees: ["나"],
      start: "2026-10-13",
      due: "2026-10-15",
      linkedTaskIds: ["t6-5"],
      labelIds: ["l_doc"],
      notes: "진척·SPI·열린 이슈를 한 장으로. 김PM 숫자와 어긋나면 현황 화면 기준으로 맞춘다.",
      checklist: [
        { id: "ck_me1", text: "진척과 SPI 붙이기", done: true },
        { id: "ck_me2", text: "지연 작업 상위 5건", done: false },
      ],
      comments: [{ id: "cm_me1", author: "김PM", body: "GAP 분석 4일 지연은 이번 초에 꼭 넣어 주세요.", at: "2026-10-15T01:20:00.000Z" }],
      activity: [{ id: "ac_me1", at: "2026-10-13T09:00:00.000Z", actor: "나", message: "생성" }],
      order: 0,
    }),
    base("x_me_slip", "기준선 대비 지연 작업 확인", {
      status: "notStarted",
      priority: "urgent",
      bucketId: "b_week",
      assignees: ["나"],
      start: "2026-10-19",
      due: "2026-10-19",
      linkedTaskIds: ["t6-1"],
      labelIds: ["l_doc"],
      notes: "착수 때 승인한 기준선과 오늘 종료일을 비교한다.",
      order: 1,
    }),
    base("x_me_steer", "운영위원회 안건 정리", {
      status: "inProgress",
      priority: "important",
      bucketId: "b_week",
      assignees: ["나"],
      start: "2026-10-19",
      due: "2026-10-21",
      linkedTaskIds: ["t6-2"],
      labelIds: ["l_meeting"],
      checklist: [
        { id: "ck_me3", text: "SSO 결정 한 줄", done: true },
        { id: "ck_me4", text: "AD 속성 지연 영향", done: false },
        { id: "ck_me5", text: "원가 계산 변경 계약", done: false },
      ],
      comments: [{ id: "cm_me2", author: "나", body: "안건은 결정·이슈·변경 요청에서 열린 것만 올립니다.", at: "2026-10-19T00:40:00.000Z" }],
      order: 2,
    }),
    base("x_me_close", "10월 3주차 현황 마감", {
      status: "notStarted",
      priority: "medium",
      bucketId: "b_week",
      assignees: ["나"],
      start: "2026-10-23",
      due: "2026-10-23",
      linkedTaskIds: ["t6-3"],
      labelIds: ["l_doc"],
      notes: "마감하면 현황의 지난 기간으로 남는다.",
      order: 3,
    }),
    base("x_me_playbook", "안정화 플레이북 목차", {
      status: "notStarted",
      priority: "low",
      bucketId: "b_backlog",
      assignees: ["나"],
      due: "2026-11-06",
      linkedTaskIds: ["t6-5"],
      labelIds: ["l_doc"],
      order: 3,
    }),
    base("x_screen_wire", "화면 설계 와이어 1차 리뷰", {
      status: "inProgress",
      priority: "important",
      bucketId: "b_week",
      assignees: ["정프론트"],
      start: "2026-10-20",
      due: "2026-10-22",
      linkedTaskIds: ["t3-3"],
      labelIds: ["l_doc"],
      checklist: [
        { id: "ck_wire1", text: "생산 현황 화면", done: true },
        { id: "ck_wire2", text: "구매 발주 화면", done: false },
      ],
      gateTags: [
        sampleGate("g_wire", "기획 확인", "spec", "ready", [
          sampleCheck("gc_wire1", "생산 현황과 구매 발주 화면이 초안에 있다", { devChecked: true }),
        ]),
      ],
      order: 1,
    }),
    base("x_model_late", "핵심 엔터티 모델 리뷰", {
      status: "notStarted",
      priority: "urgent",
      bucketId: "b_week",
      assignees: ["송DB"],
      due: "2026-10-14",
      linkedTaskIds: ["t3-2"],
      labelIds: ["l_data"],
      notes: "원가 계산 키가 아직 안 맞는다.",
      order: 1,
    }),
    base("x_fw_block", "개발 서버 방화벽 오픈 대기", {
      status: "blocked",
      priority: "urgent",
      bucketId: "b_review",
      assignees: ["강인프라"],
      due: "2026-10-17",
      linkedTaskIds: ["t4-1"],
      labelIds: ["l_bug"],
      notes: "고객사 보안팀 승인 전이라 개발 환경을 열 수 없다.",
      comments: [{ id: "cm_fw", author: "강인프라", body: "승인 티켓은 10/22 재검토입니다.", at: "2026-10-17T04:00:00.000Z" }],
      gateTags: [
        sampleGate("g_fw", "보안 검수", "security", "unverified", [
          sampleCheck("gc_fw1", "개발 서버 443 포트가 고객사에서 열린다"),
        ]),
      ],
      order: 0,
    }),
    base("x_uat_draft", "인수 시나리오 초안", {
      status: "notStarted",
      priority: "medium",
      bucketId: "b_backlog",
      assignees: ["오품질"],
      due: "2026-10-24",
      linkedTaskIds: ["t5-3"],
      labelIds: ["l_doc"],
      notes: "견적, 발주, 입고 세 장면을 고객이 따라 할 수 있게 적는다.",
      gateTags: [
        sampleGate("g_uat_draft", "고객 인수", "uat", "unverified", [
          sampleCheck("gc_uat1", "견적·발주·입고 세 장면이 시나리오에 있다"),
        ]),
      ],
      order: 0,
    }),
    base("x_me_quote", "커스터마이징 견적 정리", {
      status: "inProgress",
      priority: "important",
      bucketId: "b_week",
      assignees: ["나"],
      start: "2026-10-19",
      due: "2026-10-20",
      linkedTaskIds: ["t2-5"],
      labelIds: ["l_doc"],
      notes: "원가에 넣는 항목과 변경 계약으로 넘길 항목을 가른다. 한빛제조가 요청한 부가세 구분을 이 견적에 반영한다.",
      originIntakeId: "in_merge",
      gateTags: [
        sampleGate("g_quote", "요구 반영 #4", "qa", "unverified", [
          sampleCheck("gc_quote1", "견적서에 공급가와 부가세를 더한 합계 행이 있다"),
        ]),
      ],
      order: 4,
    }),
    base("x_if_map", "MES 인터페이스 필드 매핑", {
      status: "inProgress",
      priority: "important",
      bucketId: "b_week",
      assignees: ["박엔진"],
      start: "2026-10-21",
      due: "2026-10-27",
      linkedTaskIds: ["t3-4"],
      labelIds: ["l_data"],
      order: 2,
    }),
    base("x_charter_done", "헌장 배포 확인", {
      status: "done",
      priority: "low",
      bucketId: "b_done",
      assignees: ["김PM"],
      start: "2026-09-04",
      due: "2026-09-08",
      completedAt: "2026-09-08T02:00:00.000Z",
      linkedTaskIds: ["t1-2"],
      labelIds: ["l_doc"],
      gateTags: [
        sampleGate(
          "g_charter",
          "기획 확인",
          "spec",
          "passed",
          [sampleCheck("gc_charter1", "착수 범위와 제외 범위가 헌장에 적혀 있다", { devChecked: true, qaStatus: "pass" })],
          { verifiedBy: "최기획", verifiedAt: "2026-09-08T02:00:00.000Z" },
        ),
      ],
      order: 1,
    }),
  ];
}

/** 한빛제조 ERP에 올라와 있는 요구. 심사 전·중·승인·반려·보류가 한 번씩 있다. */
function sampleIntakes(): IntakeItem[] {
  const blank = { attachments: [] as IntakeItem["attachments"] };
  return [
    {
      ...blank,
      id: "in_vat",
      seq: 1,
      title: "견적서에 부가세 행을 넣어 달라",
      description: "현장 견적서에는 공급가만 있고 부가세가 빠진다. 영업이 매번 따로 계산하고 있다.",
      source: "client",
      type: "specChange",
      status: "pending",
      priority: "high",
      author: "정발주",
      organization: "한빛제조",
      acceptanceCriteria: ["견적서 PDF에 공급가, 부가세, 합계가 한 줄씩 보인다", "부가세는 공급가의 10%다"],
      createdAt: "2026-10-18T01:00:00.000Z",
      updatedAt: "2026-10-18T01:00:00.000Z",
    },
    {
      ...blank,
      id: "in_cost",
      seq: 2,
      title: "원가 계산 키를 품목 단위로 나누기",
      description: "지금 원가는 수주 한 건에 묶여 품목별 마진이 나오지 않는다.",
      source: "internal",
      type: "techDebt",
      status: "reviewing",
      priority: "medium",
      author: "송DB",
      estimatedEffortMM: 0.6,
      scheduleImpactDays: 4,
      acceptanceCriteria: ["품목마다 재료비와 가공비가 따로 저장된다"],
      reviewedBy: "김PM",
      createdAt: "2026-10-16T04:00:00.000Z",
      updatedAt: "2026-10-19T02:00:00.000Z",
    },
    {
      ...blank,
      id: "in_sign",
      seq: 3,
      title: "요구사항 정의서 서명 받기",
      description: "설계를 열기 전에 고객이 범위와 일정에 서명해야 한다.",
      source: "client",
      type: "newScope",
      status: "accepted",
      priority: "urgent",
      author: "정발주",
      organization: "한빛제조",
      estimatedEffortMM: 0.2,
      scheduleImpactDays: 0,
      targetScheduleTaskId: "t2-5",
      createdExecutionTaskId: "x_req_sign",
      acceptanceCriteria: ["고객 서명이 있는 PDF가 문서에 첨부된다", "정발주가 범위와 일정을 확인했다고 적는다"],
      reviewNote: "서명본이 있어야 설계를 연다.",
      reviewedBy: "김PM",
      reviewedAt: "2026-10-17T06:00:00.000Z",
      createdAt: "2026-10-15T01:00:00.000Z",
      updatedAt: "2026-10-17T06:00:00.000Z",
    },
    {
      ...blank,
      id: "in_merge",
      seq: 4,
      title: "커스터마이징 견적에 부가세 구분",
      description: "진행 중인 견적 정리에 공급가와 부가세를 나눠 적는다.",
      source: "client",
      type: "specChange",
      status: "accepted",
      priority: "high",
      author: "정발주",
      organization: "한빛제조",
      targetScheduleTaskId: "t2-5",
      targetExecutionTaskId: "x_me_quote",
      acceptanceCriteria: ["견적서에 공급가와 부가세를 더한 합계 행이 있다"],
      reviewNote: "진행 중인 견적 정리에 반영한다.",
      reviewedBy: "김PM",
      reviewedAt: "2026-10-19T03:00:00.000Z",
      createdAt: "2026-10-18T08:00:00.000Z",
      updatedAt: "2026-10-19T03:00:00.000Z",
    },
    {
      ...blank,
      id: "in_logo",
      seq: 5,
      title: "로그인 화면 로고를 더 크게",
      description: "현장 담당자가 로그인 화면에서 회사 로고가 작게 보인다고 했다.",
      source: "voc",
      type: "bug",
      status: "rejected",
      priority: "low",
      author: "현장 담당",
      organization: "한빛제조",
      acceptanceCriteria: [],
      reviewNote: "브랜드 가이드의 로고 크기라 이번 범위에서 바꾸지 않는다.",
      reviewedBy: "김PM",
      reviewedAt: "2026-10-14T07:00:00.000Z",
      createdAt: "2026-10-13T05:00:00.000Z",
      updatedAt: "2026-10-14T07:00:00.000Z",
    },
    {
      ...blank,
      id: "in_tax",
      seq: 6,
      title: "전자세금계산서 보관 기간 안내",
      description: "보관 기간 안내 문구를 견적과 세금계산서 화면에 넣을지 법무 확인이 필요하다.",
      source: "regulation",
      type: "specChange",
      status: "deferred",
      priority: "medium",
      author: "김PM",
      acceptanceCriteria: ["보관 기간이 화면과 출력물에 같은 문장으로 보인다"],
      reviewNote: "법무 확인이 끝나기 전에는 일정에 넣지 않는다. 11월에 다시 본다.",
      reviewedBy: "김PM",
      reviewedAt: "2026-10-20T01:00:00.000Z",
      createdAt: "2026-10-19T09:00:00.000Z",
      updatedAt: "2026-10-20T01:00:00.000Z",
    },
  ];
}

/**
 * Onboarding sample: a 6-month manufacturer ERP implementation (the wbsgantt case-study
 * scenario), ~40 nodes across 6 phases.
 */
export function createSampleProject(): Project {
  const tasks: Task[] = [];
  const deps: Dependency[] = [];
  let seq = 0;
  const id = (k: string) => k;
  const add = (
    key: string,
    parent: string | null,
    name: string,
    duration: number,
    extra: Partial<Task> = {},
  ): string => {
    seq++;
    tasks.push({
      id: id(key),
      parentId: parent,
      name,
      duration,
      percentComplete: 0,
      progressMethod: "percent",
      ...extra,
    });
    return key;
  };
  let dseq = 0;
  const link = (p: string, s: string, type: DependencyType = "FS", lag = 0) => {
    dseq++;
    deps.push({ id: `d${dseq}`, predecessorId: p, successorId: s, type, lag });
  };

  // 1. 착수
  add("p1", null, "착수", 0);
  add("t1-1", "p1", "킥오프 미팅", 1, { percentComplete: 100, deliverable: "킥오프 자료, 참석자 명단", assignee: "김PM", assignedResourceId: "res_kim_pm", plannedMM: 0.1, actualCost: 1_200_000 });
  add("t1-2", "p1", "프로젝트 헌장 작성", 3, { percentComplete: 100, deliverable: "프로젝트 헌장 v1.0", assignee: "김PM", assignedResourceId: "res_kim_pm", plannedMM: 0.2 });
  add("t1-3", "p1", "이해관계자 식별", 2, { percentComplete: 100, assignee: "최기획", assignedResourceId: "res_choi_plan", plannedMM: 0.15 });
  add("m1", "p1", "착수 승인", 0, { percentComplete: 100 });
  link("t1-1", "t1-2");
  link("t1-1", "t1-3", "SS", 1);
  link("t1-2", "m1");
  link("t1-3", "m1");

  // 2. 요구 분석
  add("p2", null, "요구 분석", 0);
  add("t2-1", "p2", "현업 인터뷰 (생산·구매·재무)", 8, { percentComplete: 100, assignee: "최기획", assignedResourceId: "res_choi_plan", plannedMM: 0.4 });
  add("t2-2", "p2", "AS-IS 프로세스 정리", 6, { percentComplete: 100, assignee: "최기획", assignedResourceId: "res_choi_plan", plannedMM: 0.3 });
  add("t2-3", "p2", "TO-BE 프로세스 설계", 8, { percentComplete: 80, assignee: "최기획", assignedResourceId: "res_choi_plan", plannedMM: 0.4, actualCost: 4_800_000 });
  add("t2-4", "p2", "GAP 분석 및 커스터마이징 목록", 5, { percentComplete: 40, assignee: "이아키", assignedResourceId: "res_lee_arch", plannedMM: 0.25 });
  add("t2-5", "p2", "요구사항 정의서 확정", 3, { percentComplete: 0, assignee: "김PM", assignedResourceId: "res_kim_pm", plannedMM: 0.15 });
  add("m2", "p2", "요구사항 승인", 0);
  link("m1", "t2-1");
  link("t2-1", "t2-2", "SS", 3);
  link("t2-2", "t2-3");
  link("t2-3", "t2-4", "SS", 4);
  link("t2-3", "t2-5");
  link("t2-4", "t2-5");
  link("t2-5", "m2");

  // 3. 설계
  add("p3", null, "설계", 0);
  add("t3-1", "p3", "아키텍처 설계", 5, { assignee: "이아키", assignedResourceId: "res_lee_arch", plannedMM: 0.3 });
  add("t3-2", "p3", "데이터 모델 설계", 8, { assignee: "송DB", assignedResourceId: "res_song_dba", plannedMM: 0.4 });
  add("t3-3", "p3", "화면 설계 (UI/UX)", 10, { assignee: "정프론트", assignedResourceId: "res_jung_front", plannedMM: 0.5 });
  add("t3-4", "p3", "인터페이스 설계 (MES·WMS 연동)", 6, { assignee: "박엔진", assignedResourceId: "res_park_dev", plannedMM: 0.3 });
  add("t3-5", "p3", "설계 검토회", 1, { assignee: "김PM", assignedResourceId: "res_kim_pm", plannedMM: 0.05 });
  add("m3", "p3", "설계 승인", 0);
  link("m2", "t3-1");
  link("t3-1", "t3-2");
  link("t3-1", "t3-3", "SS", 2);
  link("t3-2", "t3-4", "SS");
  link("t3-2", "t3-5");
  link("t3-3", "t3-5");
  link("t3-4", "t3-5");
  link("t3-5", "m3");

  // 4. 개발
  add("p4", null, "개발", 0);
  add("t4-1", "p4", "개발 환경 구축", 3, { assignee: "강인프라", assignedResourceId: "res_kang_infra", plannedMM: 0.2 });
  add("t4-2", "p4", "생산 모듈 개발", 20, { assignee: "박엔진", assignedResourceId: "res_park_dev", plannedMM: 1.0 });
  add("t4-3", "p4", "구매·재고 모듈 개발", 18, { assignee: "박엔진", assignedResourceId: "res_park_dev", plannedMM: 0.9 });
  add("t4-4", "p4", "재무·회계 모듈 개발", 15, { assignee: "송DB", assignedResourceId: "res_song_dba", plannedMM: 0.7 });
  add("t4-5", "p4", "인터페이스 개발", 12, { assignee: "박엔진", assignedResourceId: "res_park_dev", plannedMM: 0.6 });
  add("t4-6", "p4", "단위 테스트", 5, { assignee: "오품질", assignedResourceId: "res_oh_qa", plannedMM: 0.3 });
  add("t4-7", "p4", "데이터 이관 프로그램 개발", 8, { assignee: "송DB", assignedResourceId: "res_song_dba", plannedMM: 0.4 });
  link("m3", "t4-1");
  link("t4-1", "t4-2");
  link("t4-1", "t4-3");
  link("t4-1", "t4-4");
  link("t4-2", "t4-5", "SS", 10);
  link("t4-2", "t4-6", "FF", 3);
  link("t4-3", "t4-6", "FF", 3);
  link("t4-4", "t4-6", "FF", 3);
  link("t4-5", "t4-6");
  link("t3-2", "t4-7");

  // 5. 테스트
  add("p5", null, "테스트", 0);
  add("t5-1", "p5", "통합 테스트", 10, { assignee: "오품질", assignedResourceId: "res_oh_qa", plannedMM: 0.5 });
  add("t5-2", "p5", "결함 수정", 6, { assignee: "박엔진", assignedResourceId: "res_park_dev", plannedMM: 0.3 });
  add("t5-3", "p5", "사용자 인수 테스트", 8, { assignee: "최기획", assignedResourceId: "res_choi_plan", plannedMM: 0.4 });
  add("t5-4", "p5", "성능 테스트", 4, { assignee: "강인프라", assignedResourceId: "res_kang_infra", plannedMM: 0.2 });
  add("m5", "p5", "테스트 완료 승인", 0);
  link("t4-6", "t5-1");
  link("t5-1", "t5-2", "SS", 3);
  link("t5-2", "t5-3");
  link("t5-1", "t5-4", "SS", 5);
  link("t5-3", "m5");
  link("t5-4", "m5");

  // 6. 이행·오픈
  add("p6", null, "이행 및 오픈", 0);
  add("t6-1", "p6", "사용자 교육", 5, { assignee: "최기획", assignedResourceId: "res_choi_plan", plannedMM: 0.25 });
  add("t6-2", "p6", "데이터 이관 (리허설)", 3, { assignee: "송DB", assignedResourceId: "res_song_dba", plannedMM: 0.15 });
  add("t6-3", "p6", "데이터 이관 (본 이관)", 2, { assignee: "송DB", assignedResourceId: "res_song_dba", plannedMM: 0.1 });
  add("t6-4", "p6", "Go-Live", 0, { deadline: "2027-03-31" });
  add("t6-5", "p6", "안정화 지원", 10, { assignee: "김PM", assignedResourceId: "res_kim_pm", plannedMM: 0.5 });
  add("m6", "p6", "프로젝트 종료", 0);
  link("t5-3", "t6-1", "SS", 2);
  link("t4-7", "t6-2");
  link("t5-1", "t6-2");
  link("m5", "t6-3");
  link("t6-2", "t6-3");
  link("t6-1", "t6-4");
  link("t6-3", "t6-4");
  link("t6-4", "t6-5");
  link("t6-5", "m6");

  return {
    id: "sample-erp",
    name: "제조사 ERP 구축",
    start: "2026-09-01",
    statusDate: "2026-10-20",
    calendar: calendarWithKoreanHolidays(),
    tasks,
    dependencies: deps,
    baselines: [],
    activeBaselineId: null,
    criticalThreshold: 0,
    executionTasks: sampleExecutionTasks(),
    intakes: sampleIntakes(),
    planner: samplePlanner(),
    records: sampleErpRecords(),
    stakeholders: [
      { id: "sh_client", name: "정발주", org: "한빛제조", role: "고객 PM", interest: "high", influence: "high" },
      { id: "sh_pm", name: "김PM", org: "웨이브엑스", role: "수행 PM", interest: "high", influence: "medium" },
      { id: "sh_plan", name: "최기획", org: "웨이브엑스", role: "기획", interest: "medium", influence: "low" },
    ],
    raci: [
      { stakeholderId: "sh_pm", taskId: "p1", letter: "A" },
      { stakeholderId: "sh_client", taskId: "p1", letter: "C" },
      { stakeholderId: "sh_plan", taskId: "p1", letter: "R" },
      { stakeholderId: "sh_client", taskId: "p2", letter: "A" },
      { stakeholderId: "sh_plan", taskId: "p2", letter: "R" },
      { stakeholderId: "sh_pm", taskId: "t1-1", letter: "A" },
    ],
    costActuals: [{ id: "ca_oct", periodStart: "2026-10-01", periodEnd: "2026-10-31", amount: 8_000_000, note: "10월 공통 경비" }],
    documents: sampleErpDocuments(),
    documentLinks: sampleErpDocumentLinks(),
    resourceAssignments: sampleErpAssignments(),
    solutions: sampleErpSolutions(),
    budget: sampleErpBudget(),
    updatedAt: new Date().toISOString(),
  };
}

let sampleUploaders: Map<string, string> | null = null;

/** 샘플 첨부 id → 올린 사람. 저장본에 addedBy가 없어도 파일 목록이 같은 이름을 쓴다. */
export function sampleUploaderByAttachmentId(): ReadonlyMap<string, string> {
  if (sampleUploaders) return sampleUploaders;
  const map = new Map<string, string>();
  const take = (list: Array<{ id: string; addedBy?: string }> | undefined) => {
    for (const item of list ?? []) if (item.addedBy) map.set(item.id, item.addedBy);
  };
  const project = createSampleProject();
  for (const record of project.records ?? []) {
    take(record.attachments);
    for (const comment of record.comments) take(comment.attachments);
  }
  for (const task of project.executionTasks) {
    take(task.attachments);
    for (const comment of task.comments) take(comment.attachments);
  }
  sampleUploaders = map;
  return map;
}

export interface DemoCatalog {
  teams: Record<string, OrganizationTeam>;
  resources: Record<string, UserResource>;
  projects: Record<string, Project>;
}

/** Full demo pack: 8 teams, 18 people, two plans in each sample workspace. */
export function createDemoCatalog(): DemoCatalog {
  const teams: Record<string, OrganizationTeam> = {};
  for (const t of sampleOrganizationTeams()) teams[t.id] = t;
  const resources: Record<string, UserResource> = {};
  for (const r of sampleUserResources()) resources[r.id] = r;
  const erp = createSampleProject();
  const quote = createSampleQuoteProject();
  const robot = createSampleRobotProject();
  const safety = createSampleSafetyProject();
  const marketing = createSampleMarketingProject();
  const content = createSampleContentProject();
  return {
    teams,
    resources,
    projects: {
      [erp.id]: erp,
      [quote.id]: quote,
      [robot.id]: robot,
      [safety.id]: safety,
      [marketing.id]: marketing,
      [content.id]: content,
    },
  };
}

export function createEmptyProject(name = "새 플랜", start = new Date().toISOString().slice(0, 10)): Project {
  return {
    id: `p_${Date.now().toString(36)}`,
    name,
    start,
    statusDate: start,
    calendar: calendarWithKoreanHolidays(),
    tasks: [
      { id: "t1", parentId: null, name: "첫 작업", duration: 5, percentComplete: 0, progressMethod: "percent" },
    ],
    dependencies: [],
    baselines: [],
    activeBaselineId: null,
    criticalThreshold: 0,
    executionTasks: [],
    planner: {
      buckets: [
        { id: "b_todo", name: "할 일" },
        { id: "b_week", name: "이번 주" },
        { id: "b_later", name: "나중에" },
      ],
      labels: [
        { id: "l_bug", name: "버그", color: "red" },
        { id: "l_review", name: "리뷰 필요", color: "orange" },
        { id: "l_doc", name: "문서", color: "blue" },
        { id: "l_meeting", name: "회의", color: "purple" },
      ],
      customFields: [],
    },
    records: [],
    documents: [],
    documentLinks: [],
    resourceAssignments: [],
    solutions: [],
    budget: {
      totalBudget: 100000000,
      targetLaborCost: 70000000,
      targetSolutionCost: 20000000,
      targetExpenseCost: 5000000,
      contingencyCost: 5000000,
      costOptions: defaultCostOptions(),
    },
    updatedAt: new Date().toISOString(),
  };
}
